import MapView, { Marker, PROVIDER_GOOGLE } from 'react-native-maps';
import { useEffect } from 'react';
import * as Location from 'expo-location';
import { View, useColorScheme } from 'react-native';
import AddBusinessButton from './addBusinessButton';
import darkMapStyle from './map-styles/dark.json';
import lightMapStyle from './map-styles/light.json';
import DrawerButton from './drawerButton';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { setSelectedBusiness } from '../redux/businessListSlice';
import { setMobileMapWindow } from '../redux/mapSlice';
import { setUsersLocation } from '../redux/userSlice';
import { Business } from '../types';
import CurrentLocationButton from './currentLocationButton';
import { BottomSheetMethods } from '@gorhom/bottom-sheet/lib/typescript/types';
import { useAppTheme } from '../theme';
import Fuse from 'fuse.js';

interface Props {
  mapRef: React.RefObject<MapView>;
  bottomSheetRef: React.RefObject<BottomSheetMethods>;
}

export default function MobileMap({ mapRef, bottomSheetRef }: Props) {
  const dispatch = useAppDispatch();
  const theme = useAppTheme();
  const colorScheme = useColorScheme();

  const { businesses, selectedBusiness, filterOptions, businessSearchText } =
    useAppSelector((state) => state.businessList);

  const { usersLocation } = useAppSelector((state) => state.user);
  const { mobileMapWindow } = useAppSelector((state) => state.map);

  useEffect(() => {
    (async () => {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        console.log('Permission to access location was denied');
        return;
      }

      const location = await Location.getCurrentPositionAsync({});
      const { latitude, longitude } = location.coords;
      // reset map initial position to user position
      // might need a way to get org position instead
      dispatch(setUsersLocation({ latitude, longitude }));
    })();
  }, []);

  async function onMapMarkerPress(business: Business) {
    dispatch(setSelectedBusiness(business));

    bottomSheetRef.current?.snapToIndex(2);

    mapRef.current?.animateToRegion(
      {
        latitude: business.latitude - 0.005,
        longitude: business.longitude,
        latitudeDelta: mobileMapWindow.latitudeDelta,
        longitudeDelta: mobileMapWindow.longitudeDelta,
      },
      500
    );
  }

  function getStatusColor(business: Business) {
    let color = theme.colors.openMarkerColor;

    if (business.status === 'Closed') {
      color = theme.colors.closedMarkerColor;
    } else if (business.status === 'Active') {
      color = theme.colors.activeMarkerColor;
    }

    return color;
  }

  function getMarkerOpacity(business: Business) {
    let opacity = 1;

    if (selectedBusiness && business.id !== selectedBusiness.id) {
      opacity = 0.5;
    }

    return opacity;
  }

  const filteredBusinesses = businesses.filter(
    (b) => filterOptions.length === 0 || filterOptions.includes(b.status)
  );

  const fuse = new Fuse(filteredBusinesses, {
    keys: ['name', 'address'],
    isCaseSensitive: false,
    includeScore: true,
    threshold: 0.4,
  });

  const searchResults =
    businessSearchText !== ''
      ? fuse.search(businessSearchText).map((s) => s.item)
      : filteredBusinesses;

  return (
    <>
      <MapView
        provider={PROVIDER_GOOGLE}
        ref={mapRef}
        showsUserLocation
        showsCompass={false}
        showsPointsOfInterest={false}
        showsMyLocationButton={false}
        //   options prop available on react-native-web-maps, not RN maps
        userLocationPriority="low"
        // initial region location
        region={{
          latitude: usersLocation.latitude,
          longitude: usersLocation.longitude,
          latitudeDelta: 0.1,
          longitudeDelta: 0.1,
        }}
        style={{ width: '100%', height: '100%' }}
        customMapStyle={colorScheme === 'dark' ? darkMapStyle : lightMapStyle}
        // can set latitude & long delta here
        // setting whole region causes continual shift
        onRegionChangeComplete={(e) => {
          // keep track of current window, but don't impact region
          dispatch(
            setMobileMapWindow({
              latitudeDelta: e.latitudeDelta,
              longitudeDelta: e.longitudeDelta,
            })
          );
        }}
        onPress={(e) => {
          if (e.nativeEvent.action != 'marker-press' && selectedBusiness) {
            // return bottom sheet to list view
            dispatch(setSelectedBusiness(null));
          }
        }}
      >
        {searchResults.map((business) => {
          return (
            <Marker
              key={business.id}
              flat={true}
              pinColor={getStatusColor(business)}
              onPress={() => onMapMarkerPress(business)}
              opacity={getMarkerOpacity(business)}
              coordinate={{
                latitude: business.latitude,
                longitude: business.longitude,
              }}
            />
          );
        })}
      </MapView>
      <View
        style={{
          position: 'absolute',
          top: 50,
          left: 0,
        }}
      >
        <DrawerButton />
      </View>
      <View style={{ position: 'absolute', top: 50, right: 15 }}>
        <AddBusinessButton />
      </View>
      <View style={{ position: 'absolute', top: 100, right: 12 }}>
        <CurrentLocationButton mapRef={mapRef} />
      </View>
    </>
  );
}
